import type { Overview, OverviewRange } from "@unbox/api";
import { useMemo } from "react";
import { useMetricsOverview } from "@/api/metrics-overview";
import { formatDateByPeriod, getWeekNumber } from "@/pages/reports/utils";
import {
  formatCurrency as utilFormatCurrency,
  formatNumber as utilFormatNumber,
  formatPercentage as utilFormatPercentage,
} from "@/utils/formatNumber";
import { useCurrentCurrency } from "@/utils/useCurrentCurrency";

export type ChartDataPoint = {
  week: string;
  value: number;
};
export type KPIData = {
  value: string;
  change: string;
  trend: "up" | "down";
  description: string;
  chartData: ChartDataPoint[];
  priorPeriod?: {
    from: string;
    to: string;
    value: number;
  };
  currentPeriod?: {
    from: string;
    to: string;
    value: number;
  };
};
export interface SimpleKPIData {
  value: string;
  change: string;
  trend: "up" | "down";
  description: string;
}

export interface Product {
  id: string;
  name: string;
  gmv: string;
  percentage: string;
  units: number;
}

export interface Creator {
  id: string;
  name: string;
  gmv: string;
  percentage: string;
  units: number;
}
export interface DashboardData {
  mainKPIs: {
    weeklyGMV: KPIData;
    weeklyVideos: KPIData;
    weeklyCreators: KPIData;
  };
  revenueKPIs: {
    weeklyProductImpressions: KPIData;
    weeklyAOV: KPIData;
    unitSalesRevenue: KPIData;
  };
  creatorKPIs: {
    weeklyNewCreators: KPIData;
    averageCreatorGMV: KPIData;
  };
  videoKPIs: {
    weeklyRevenueDrivingVideos: KPIData;
    weeklyAverageVideoGMV: KPIData;
  };
  topProducts: Product[];
  topCreators: Creator[];
  currentWeek?: number; // Optional: only present for legacy Overview data
}

const calculateChange = (
  current: number | null | undefined,
  previous: number | null | undefined,
): { change: string; trend: "up" | "down" } => {
  const curr = current || 0;
  const prev = previous || 0;

  if (prev === 0) {
    return {
      change: curr > 0 ? "+100%" : "0%",
      trend: curr > 0 ? "up" : "down",
    };
  }

  const changePercent = ((curr - prev) / prev) * 100;
  const sign = changePercent >= 0 ? "+" : "";

  return {
    change: `${sign}${changePercent.toFixed(1)}%`,
    trend: changePercent >= 0 ? "up" : "down",
  };
};

// Sort time period data chronologically by date
export const sortTimePeriodDataByDate = <T extends { periodStart?: string }>(data: T[]): T[] => {
  if (!data || data.length === 0) return data;

  return [...data].sort((a, b) => {
    const dateA = new Date(a.periodStart || "");
    const dateB = new Date(b.periodStart || "");
    return dateA.getTime() - dateB.getTime();
  });
};

// Transform API weekly data to chart format
const transformWeeklyData = (
  week: number,
  weeklyData: Array<{ periodStart?: string; value: number }>,
): ChartDataPoint[] => {
  const offset = week - weeklyData.length;
  return weeklyData.map((item, index) => {
    const weekNumber = offset + index + 1;
    const date = item.periodStart ? new Date(item.periodStart) : new Date();
    const formattedWeek = formatDateByPeriod(date, "weekly", weekNumber);

    return {
      week: formattedWeek,
      value: item.value || 0,
    };
  });
};

// Get current and previous week values from time series (assumes data is already sorted)
const getCurrentAndPrevious = (
  weeklyData: Array<{ periodStart: string; value: number }>,
): { current: number; previous: number } => {
  if (!weeklyData || weeklyData.length === 0) {
    return { current: 0, previous: 0 };
  }

  const values = sortTimePeriodDataByDate(weeklyData).map((item) => {
    const value = item.value;
    return value == null || Number.isNaN(value) ? 0 : value;
  });
  const current = values[values.length - 1] || 0;
  const previous = values[values.length - 2] || 0;
  return { current, previous };
};

// Get the latest week from API data
const getLatestWeek = (timePeriodData: Array<{ periodStart?: string }>): number => {
  if (timePeriodData.length === 0) return getWeekNumber(new Date());

  const latestItem = timePeriodData[timePeriodData.length - 1];
  const dateField = latestItem.periodStart;

  if (dateField) {
    return getWeekNumber(new Date(dateField));
  }

  return getWeekNumber(new Date());
};

// Type for legacy Overview (excludes OverviewRange)
type LegacyOverview = Exclude<NonNullable<Overview>, { kpis: any; chartData: any }>;

// Transform API response to Dashboard data format
const transformApiData = (apiData: LegacyOverview, currencySymbol: string): DashboardData => {
  // Get the current week from the most recent data point
  const currentWeek = getLatestWeek(apiData.weeklyRevenue);
  // Transform products
  const totalRevenue = apiData.totalRevenue;
  const topProducts: Product[] = apiData.topProducts.map((product) => ({
    id: product.productId,
    name: product.productName,
    gmv: utilFormatCurrency(currencySymbol, product.totalRevenue),
    percentage: utilFormatPercentage(totalRevenue > 0 ? product.totalRevenue / totalRevenue : 0),
    units: product.unitSales,
  }));

  // Transform creators
  const topCreators: Creator[] = apiData.topCreators.map((creator) => ({
    id: creator.creatorId,
    name: `@${creator.creatorName}`,
    gmv: utilFormatCurrency(currencySymbol, creator.totalRevenue),
    percentage: utilFormatPercentage(totalRevenue > 0 ? creator.totalRevenue / totalRevenue : 0),
    units: creator.unitSales,
  }));

  // Get current week values and calculate changes
  const revenueData = getCurrentAndPrevious(apiData.weeklyRevenue);
  const revenueChange = calculateChange(revenueData.current, revenueData.previous);

  const videosData = getCurrentAndPrevious(apiData.videosPosted);
  const videosChange = calculateChange(videosData.current, videosData.previous);

  const creatorsData = getCurrentAndPrevious(apiData.creatorsPosted);
  const creatorsChange = calculateChange(creatorsData.current, creatorsData.previous);

  const aovData = getCurrentAndPrevious(apiData.weeklyAov);
  const aovChange = calculateChange(aovData.current, aovData.previous);

  const newCreatorsData = getCurrentAndPrevious(apiData.newCreators);
  const newCreatorsChange = calculateChange(newCreatorsData.current, newCreatorsData.previous);

  const avgCreatorData = getCurrentAndPrevious(apiData.avgCreatorGmv);
  const avgCreatorChange = calculateChange(avgCreatorData.current, avgCreatorData.previous);

  const unitSalesData = getCurrentAndPrevious(apiData.weeklyUnitSales);
  const unitSalesChange = calculateChange(unitSalesData.current, unitSalesData.previous);

  const viewsData = getCurrentAndPrevious(apiData.weeklyViews);
  const viewsChange = calculateChange(viewsData.current, viewsData.previous);

  const weeklyRevenueDrivingVideosData = getCurrentAndPrevious(apiData.weeklyRevenueDrivingVideos);
  const weeklyRevenueDrivingVideosChange = calculateChange(
    weeklyRevenueDrivingVideosData.current,
    weeklyRevenueDrivingVideosData.previous,
  );

  const weeklyAverageVideoGMVData = getCurrentAndPrevious(apiData.weeklyAverageVideoGMV);
  const weeklyAverageVideoGMVChange = calculateChange(
    weeklyAverageVideoGMVData.current,
    weeklyAverageVideoGMVData.previous,
  );

  return {
    mainKPIs: {
      weeklyGMV: {
        value: utilFormatCurrency(currencySymbol, revenueData.current),
        change: revenueChange.change,
        trend: revenueChange.trend,
        description: "Weekly GMV",
        chartData: transformWeeklyData(currentWeek, apiData.weeklyRevenue),
      },
      weeklyVideos: {
        value: utilFormatNumber(videosData.current, true),
        change: videosChange.change,
        trend: videosChange.trend,
        description: "Weekly Videos Posted",
        chartData: transformWeeklyData(currentWeek, apiData.videosPosted),
      },
      weeklyCreators: {
        value: utilFormatNumber(creatorsData.current, true),
        change: creatorsChange.change,
        trend: creatorsChange.trend,
        description: "Weekly Creators Posting",
        chartData: transformWeeklyData(currentWeek, apiData.creatorsPosted),
      },
    },
    revenueKPIs: {
      weeklyProductImpressions: {
        value: utilFormatNumber(viewsData.current, true),
        change: viewsChange.change,
        trend: viewsChange.trend,
        description: "Weekly Product Views",
        chartData: transformWeeklyData(currentWeek, apiData.weeklyViews),
      },
      weeklyAOV: {
        value: utilFormatCurrency(currencySymbol, aovData.current),
        change: aovChange.change,
        trend: aovChange.trend,
        description: "Weekly AOV",
        chartData: transformWeeklyData(currentWeek, apiData.weeklyAov),
      },
      unitSalesRevenue: {
        value: utilFormatNumber(unitSalesData.current, true),
        change: unitSalesChange.change,
        trend: unitSalesChange.trend,
        description: "Weekly Unit Sales",
        chartData: transformWeeklyData(currentWeek, apiData.weeklyUnitSales),
      },
    },
    creatorKPIs: {
      weeklyNewCreators: {
        value: utilFormatNumber(newCreatorsData.current, true),
        change: newCreatorsChange.change,
        trend: newCreatorsChange.trend,
        description: "Weekly New Creators",
        chartData: transformWeeklyData(currentWeek, apiData.newCreators),
      },
      averageCreatorGMV: {
        value: utilFormatCurrency(currencySymbol, avgCreatorData.current),
        change: avgCreatorChange.change,
        trend: avgCreatorChange.trend,
        description: "Weekly Average Creator GMV",
        chartData: transformWeeklyData(currentWeek, apiData.avgCreatorGmv),
      },
    },
    videoKPIs: {
      weeklyAverageVideoGMV: {
        value: utilFormatCurrency(currencySymbol, weeklyAverageVideoGMVData.current),
        change: weeklyAverageVideoGMVChange.change,
        trend: weeklyAverageVideoGMVChange.trend,
        description: "Weekly Average Video GMV",
        chartData: transformWeeklyData(currentWeek, apiData.weeklyAverageVideoGMV),
      },
      weeklyRevenueDrivingVideos: {
        value: utilFormatNumber(weeklyRevenueDrivingVideosData.current, true),
        change: weeklyRevenueDrivingVideosChange.change,
        trend: weeklyRevenueDrivingVideosChange.trend,
        description: "Weekly Revenue Driving Videos",
        chartData: transformWeeklyData(currentWeek, apiData.weeklyRevenueDrivingVideos),
      },
    },
    topProducts,
    topCreators,
    currentWeek,
  };
};

// Transform OverviewRange API response to Dashboard data format
const transformRangeData = (apiData: OverviewRange, currencySymbol: string): DashboardData => {
  const formatChange = (change: number): string => {
    const sign = change >= 0 ? "+" : "";
    return `${sign}${change.toFixed(1)}%`;
  };

  // Transform products
  const topProducts: Product[] = apiData.topProducts.map((product) => ({
    id: product.productId,
    name: product.productName,
    gmv: utilFormatCurrency(currencySymbol, product.revenue),
    percentage: utilFormatPercentage(product.revenuePercentage / 100),
    units: product.unitSales,
  }));

  // Transform creators
  const topCreators: Creator[] = apiData.topCreators.map((creator) => ({
    id: creator.creatorId,
    name: `@${creator.creatorName}`,
    gmv: utilFormatCurrency(currencySymbol, creator.revenue),
    percentage: utilFormatPercentage(creator.revenuePercentage / 100),
    units: creator.unitSales,
  }));

  // Transform chart data to match expected format
  const transformChartData = (metricKey: keyof OverviewRange["chartData"]["periods"][0]): ChartDataPoint[] => {
    const bucket = apiData.chartData.bucket;
    const timePeriod = bucket === "daily" ? "daily" : bucket === "weekly" ? "weekly" : "monthly";

    return apiData.chartData.periods.map((period, index) => {
      const date = new Date(period.periodStart);
      // formatDateByPeriod will automatically calculate week number from date for weekly periods
      const formattedDate = formatDateByPeriod(date, timePeriod);

      return {
        week: formattedDate,
        value: period[metricKey] as number,
      };
    });
  };

  return {
    mainKPIs: {
      weeklyGMV: {
        value: utilFormatCurrency(currencySymbol, apiData.kpis.gmv.current),
        change: formatChange(apiData.kpis.gmv.change),
        trend: apiData.kpis.gmv.change >= 0 ? "up" : "down",
        description: "GMV",
        chartData: transformChartData("gmv"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.gmv.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.gmv.prior,
        },
      },
      weeklyVideos: {
        value: utilFormatNumber(apiData.kpis.videos.current, true),
        change: formatChange(apiData.kpis.videos.change),
        trend: apiData.kpis.videos.change >= 0 ? "up" : "down",
        description: "Videos Posted",
        chartData: transformChartData("videos"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.videos.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.videos.prior,
        },
      },
      weeklyCreators: {
        value: utilFormatNumber(apiData.kpis.creators.current, true),
        change: formatChange(apiData.kpis.creators.change),
        trend: apiData.kpis.creators.change >= 0 ? "up" : "down",
        description: "Creators Posting",
        chartData: transformChartData("creators"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.creators.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.creators.prior,
        },
      },
    },
    revenueKPIs: {
      weeklyProductImpressions: {
        value: utilFormatNumber(apiData.kpis.productViews.current, true),
        change: formatChange(apiData.kpis.productViews.change),
        trend: apiData.kpis.productViews.change >= 0 ? "up" : "down",
        description: "Product Views",
        chartData: transformChartData("productViews"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.productViews.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.productViews.prior,
        },
      },
      weeklyAOV: {
        value: utilFormatCurrency(currencySymbol, apiData.kpis.aov.current),
        change: formatChange(apiData.kpis.aov.change),
        trend: apiData.kpis.aov.change >= 0 ? "up" : "down",
        description: "AOV",
        chartData: transformChartData("aov"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.aov.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.aov.prior,
        },
      },
      unitSalesRevenue: {
        value: utilFormatNumber(apiData.kpis.unitSales.current, true),
        change: formatChange(apiData.kpis.unitSales.change),
        trend: apiData.kpis.unitSales.change >= 0 ? "up" : "down",
        description: "Unit Sales",
        chartData: transformChartData("unitSales"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.unitSales.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.unitSales.prior,
        },
      },
    },
    creatorKPIs: {
      weeklyNewCreators: {
        value: utilFormatNumber(apiData.kpis.newCreators.current, true),
        change: formatChange(apiData.kpis.newCreators.change),
        trend: apiData.kpis.newCreators.change >= 0 ? "up" : "down",
        description: "New Creators",
        chartData: transformChartData("newCreators"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.newCreators.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.newCreators.prior,
        },
      },
      averageCreatorGMV: {
        value: utilFormatCurrency(currencySymbol, apiData.kpis.avgCreatorGmv.current),
        change: formatChange(apiData.kpis.avgCreatorGmv.change),
        trend: apiData.kpis.avgCreatorGmv.change >= 0 ? "up" : "down",
        description: "Average Creator GMV",
        chartData: transformChartData("avgCreatorGmv"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.avgCreatorGmv.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.avgCreatorGmv.prior,
        },
      },
    },
    videoKPIs: {
      weeklyAverageVideoGMV: {
        value: utilFormatCurrency(currencySymbol, apiData.kpis.avgVideoGmv.current),
        change: formatChange(apiData.kpis.avgVideoGmv.change),
        trend: apiData.kpis.avgVideoGmv.change >= 0 ? "up" : "down",
        description: "Average Video GMV",
        chartData: transformChartData("avgVideoGmv"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.avgVideoGmv.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.avgVideoGmv.prior,
        },
      },
      weeklyRevenueDrivingVideos: {
        value: utilFormatNumber(apiData.kpis.revenueDrivingVideos.current, true),
        change: formatChange(apiData.kpis.revenueDrivingVideos.change),
        trend: apiData.kpis.revenueDrivingVideos.change >= 0 ? "up" : "down",
        description: "Revenue Driving Videos",
        chartData: transformChartData("revenueDrivingVideos"),
        currentPeriod: {
          from: apiData.period.from,
          to: apiData.period.to,
          value: apiData.kpis.revenueDrivingVideos.current,
        },
        priorPeriod: {
          from: apiData.priorPeriod.from,
          to: apiData.priorPeriod.to,
          value: apiData.kpis.revenueDrivingVideos.prior,
        },
      },
    },
    topProducts,
    topCreators,
  };
};

export const useDashboardData = (
  timePeriod: "daily" | "weekly" | "monthly" = "weekly",
  dateRange?: { from: string; to: string },
) => {
  const { data, ...rest } = useMetricsOverview(timePeriod, dateRange);
  const currencySymbol = useCurrentCurrency();
  const dashboardData = useMemo(() => {
    if (!data) return undefined;

    // Check if data is OverviewRange (has kpis and chartData properties)
    if ("kpis" in data && "chartData" in data) {
      return transformRangeData(data, currencySymbol);
    }

    // Legacy Overview data
    return transformApiData(data as LegacyOverview, currencySymbol);
  }, [data, currencySymbol]);

  return { data: dashboardData, ...rest };
};
